import { cn } from '@/lib/utils';
import type { MatrixViewStateShape } from '@mss_creator/shared';
import { Button, Icon } from '@pms-digital/design-system';
import { useState } from 'react';
import { NewMatrixMenu } from './new-matrix-menu';
import { ZOOM_STEP_MAX, ZOOM_STEP_MIN, type ZoomStep } from './zoom';

/** The notes controls of the bar (issue #19): the Notes view button and the eye that hides the markers. */
export interface ToolbarNotes {
  /** True while `?vue=notes` is showing — the button is a mode toggle, so it has to read as pressed. */
  open: boolean;
  /** Open discussions of the matrix visible to the viewer; the badge is absent for zero. */
  openCount: number;
  /** Toggles `?vue=notes` on the current route. */
  onToggle: () => void;
  /** The viewer hid the markers and the « + » bubbles of the grid (per user and per matrix). */
  markersHidden: boolean;
  onToggleMarkers: () => void;
}

export interface MatrixToolbarProps {
  canUndo: boolean;
  canRedo: boolean;
  /** The whole shared view state — the toolbar derives its zoom readout and its
   * "Tout afficher" active state from it rather than taking two extra props. */
  viewState: MatrixViewStateShape;
  multiSelectMode: boolean;
  /**
   * Global (fit-to-screen, read-only) view mode — per-viewer and per-matrix,
   * see `lib/matrix-view-mode-store.ts`. Unlike `viewState`, it is never shared
   * between viewers (2026-08-25 design doc, S1).
   */
  globalView: boolean;
  onToggleGlobalView: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onShowAll: () => void;
  onResetSizes: () => void;
  onToggleMultiSelect: () => void;
  /** True while `?vue=cadrage` is showing — the button is a mode toggle, so it has to read as pressed. */
  cadrageOpen: boolean;
  /** Toggles `?vue=cadrage` on the current route (issue #3). */
  onToggleCadrage: () => void;
  /** Navigates to the export page (2026-08-09 design doc) — the toolbar only reports intent, same as every other button here. */
  onExport: () => void;
  /** Notes view button and « Masquer les repères » eye (issue #19). Absent, the bar has neither. */
  notes?: ToolbarNotes;
  /** « Matrice vierge »: creates an empty matrix for the current matrix's company and navigates to the wizard. */
  onCreateMatrix: () => void;
  /** « Depuis cette matrice » is offered (not while generating, nor after a failure — issue #16). */
  canDeriveFromThis: boolean;
  /** « Depuis cette matrice »: derives a pre-filled draft and opens its wizard; settles either way. */
  onDeriveFromThis: () => Promise<void>;
  /** The last derive failure, shown under the menu. */
  deriveError: string | null;
}

/**
 * Labels for the 4 discrete zoom steps (design doc M12). The mechanism stays
 * E13's discrete Tailwind text sizes — these percentages are a *readout*, not
 * a `transform: scale()` factor (Écran 30's continuous scaling is explicitly
 * not adopted).
 */
const ZOOM_PERCENT_LABELS: Record<ZoomStep, string> = {
  0: '85%',
  1: '100%',
  2: '115%',
  3: '130%',
};

/** True when anything is hidden or collapsed — drives "Tout afficher"'s active state (M13). */
function hasHiddenItems(viewState: MatrixViewStateShape): boolean {
  return (
    viewState.hiddenColumns.length > 0 ||
    viewState.hiddenDimensionIds.length > 0 ||
    viewState.activationRowCollapsed
  );
}

function ToolbarSeparator() {
  return <div aria-hidden="true" className="mx-1 h-6 w-px bg-gray-100" />;
}

interface ToolbarOverflowMenuProps {
  showAllActive: boolean;
  onShowAll: () => void;
  onResetSizes: () => void;
}

/**
 * Collapses "Tout afficher" / "Réinitialiser les largeurs/hauteurs" into a
 * `⋯` menu once the toolbar's container width drops below the `@sm`
 * container-query breakpoint (point 2) — Undo/Redo/zoom/Sélection/Export
 * never collapse, they're rendered directly in `MatrixToolbar` outside this
 * component.
 */
function ToolbarOverflowMenu({ showAllActive, onShowAll, onResetSizes }: ToolbarOverflowMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {/* Inline at container width >= @sm, hidden below it. */}
      <div className="@sm:flex hidden items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          compact
          onClick={onShowAll}
          data-active={showAllActive ? 'true' : undefined}
          title="Réaffiche toutes les colonnes et dimensions masquées"
        >
          Tout afficher
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          compact
          onClick={onResetSizes}
          title="Rétablit la largeur et la hauteur par défaut de toutes les colonnes et lignes"
        >
          Réinitialiser les largeurs/hauteurs
        </Button>
      </div>

      {/* Collapsed trigger, only shown below @sm. */}
      <div className="relative @sm:hidden">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Plus d’actions"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          <Icon name="more_horiz" size={16} />
        </Button>
        {isOpen && (
          <div
            role="menu"
            className="absolute top-full left-0 z-20 mt-1 flex w-64 flex-col rounded-md border border-gray-200 bg-white p-1 shadow-lg"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onShowAll();
                setIsOpen(false);
              }}
              data-active={showAllActive ? 'true' : undefined}
              className={cn(
                'flex items-center gap-2 rounded px-2 py-1 text-left text-small hover:bg-gray-100',
                showAllActive && 'bg-bleu-nuit-tint text-bleu-nuit',
              )}
            >
              {showAllActive && <Icon name="check" size={16} />}
              Tout afficher
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onResetSizes();
                setIsOpen(false);
              }}
              className="rounded px-2 py-1 text-left text-small hover:bg-gray-100"
            >
              Réinitialiser les largeurs/hauteurs
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Button-group header rendered above the grid (E11, restyled to the mockup by
 * M12/M13): undo, redo, a −/percentage/+ zoom control, "Tout afficher" with an
 * active state, "Réinitialiser les largeurs/hauteurs", the multi-select toggle
 * (M11), and "Exporter" — navigates to the export page (2026-08-09 design doc);
 * no longer the inert M13 shell now that the export page itself exists.
 *
 * Undo/redo reflect `matrix.canUndo`/`canRedo`; the corresponding
 * `Ctrl+Z`/`Ctrl+Shift+Z` shortcuts (E17) are wired by the parent route, not
 * here. The mockup shows no undo/redo buttons at all — M13 keeps them anyway,
 * since unlike the mockup they're wired to a real actions log.
 */
export function MatrixToolbar({
  canUndo,
  canRedo,
  viewState,
  multiSelectMode,
  globalView,
  onToggleGlobalView,
  onUndo,
  onRedo,
  onZoomIn,
  onZoomOut,
  onShowAll,
  onResetSizes,
  onToggleMultiSelect,
  cadrageOpen,
  onToggleCadrage,
  notes,
  onExport,
  onCreateMatrix,
  canDeriveFromThis,
  onDeriveFromThis,
  deriveError,
}: MatrixToolbarProps) {
  const zoomStep = viewState.zoomStep;
  const showAllActive = hasHiddenItems(viewState);

  return (
    <div
      data-testid="matrix-toolbar-root"
      className="@container flex min-w-0 flex-wrap items-center gap-1 bg-white p-2"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onUndo}
        disabled={!canUndo}
        aria-label="Annuler"
        title="Annuler (Ctrl+Z)"
      >
        <Icon name="undo" size={16} />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRedo}
        disabled={!canRedo}
        aria-label="Rétablir"
        title="Rétablir (Ctrl+Maj+Z)"
      >
        <Icon name="redo" size={16} />
      </Button>

      <ToolbarSeparator />

      {/* −/readout/+ group (M12). The readout is a label over the same 4
          discrete steps; it never becomes a free-form input. */}
      <div className="flex h-9 items-center gap-1 rounded-md border border-gray-200 px-1">
        <button
          type="button"
          onClick={onZoomOut}
          disabled={globalView || zoomStep <= ZOOM_STEP_MIN}
          aria-label="Réduire le zoom"
          className="flex h-7 w-7 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <Icon name="remove" size={16} />
        </button>
        <span
          aria-live="polite"
          data-testid="zoom-readout"
          className={cn(
            'w-11 text-center font-semibold text-caption',
            globalView ? 'text-gray-600' : 'text-bleu-nuit',
          )}
        >
          {/* A word, not a percentage: the discrete zoom steps do not apply in
              global view, and showing a stale "100%" at a visible ~45% would
              simply be false. The readout is a label, never an input. */}
          {globalView ? 'Ajusté' : ZOOM_PERCENT_LABELS[zoomStep]}
        </span>
        <button
          type="button"
          onClick={onZoomIn}
          disabled={globalView || zoomStep >= ZOOM_STEP_MAX}
          aria-label="Agrandir le zoom"
          className="flex h-7 w-7 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <Icon name="add" size={16} />
        </button>
      </div>

      <ToolbarSeparator />

      <ToolbarOverflowMenu
        showAllActive={showAllActive}
        onShowAll={onShowAll}
        onResetSizes={onResetSizes}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        compact
        onClick={onToggleMultiSelect}
        aria-pressed={multiSelectMode}
        // Disabled in global view: multi-select's only action is "régénérer la
        // sélection", which routes through the drawer, and the drawer is absent
        // there (2026-08-25 design doc, R5/F2).
        disabled={globalView}
        title="Sélectionner plusieurs cellules à la fois"
      >
        <Icon name="check_box" size={16} />
        Sélection
      </Button>

      {/* A distinct mode switch, not a fifth zoom step: it turns editing off,
          and a zoom control must never do that (design doc L1). Rendered here
          rather than inside ToolbarOverflowMenu so it never collapses — same
          treatment as zoom, Sélection and Exporter (L4). */}
      <Button
        type="button"
        variant={globalView ? 'secondary' : 'ghost'}
        size="sm"
        compact
        onClick={onToggleGlobalView}
        aria-pressed={globalView}
        title="Affiche toute la matrice à l’écran, en lecture seule"
      >
        <Icon name="close_fullscreen" size={16} />
        Vue globale
      </Button>
      {/* Explicit wording for the read-only state, inline so it costs no
          vertical space — a banner strip would take ~5% off the fit factor of
          every matrix, in the very mode built to make things fit (R8). */}
      {globalView && (
        <span className="font-semibold text-caption text-gray-600">lecture seule</span>
      )}

      {/* The Cadrage tab's entry point (issue #3). A pressed toggle rather
          than a link-out, because that is what it is: the grid stays mounted
          behind it and clicking again returns to exactly the zoom, selection
          and scroll position it was left at. Rendered outside
          ToolbarOverflowMenu so it never collapses — same treatment as zoom,
          Sélection, Vue globale and Exporter (L4). */}
      <Button
        type="button"
        variant={cadrageOpen ? 'secondary' : 'ghost'}
        size="sm"
        compact
        onClick={onToggleCadrage}
        aria-pressed={cadrageOpen}
        title="Sujet, contexte, paliers, documents et prompt de cette matrice"
      >
        <Icon name="format_align_left" size={16} />
        Cadrage
      </Button>

      {/* Issue #19: the Notes view, at the same rank as Cadrage, then the eye. */}
      {notes && (
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            compact
            onClick={notes.onToggle}
            aria-pressed={notes.open}
            title="Le mémo et toutes les discussions de cette matrice"
          >
            <Icon name="chat_bubble" size={16} />
            Notes
            {notes.openCount > 0 && (
              <span
                data-testid="notes-open-count"
                className="rounded-full bg-gray-100 px-1 font-semibold text-gray-700 text-micro"
              >
                {notes.openCount}
              </span>
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={notes.onToggleMarkers}
            aria-pressed={notes.markersHidden}
            aria-label={notes.markersHidden ? 'Afficher les repères' : 'Masquer les repères'}
            title={
              notes.markersHidden
                ? 'Afficher les repères de notes dans la grille'
                : 'Masquer les repères de notes dans la grille'
            }
          >
            {notes.markersHidden ? (
              <Icon name="visibility_off" size={16} />
            ) : (
              <Icon name="visibility" size={16} />
            )}
          </Button>
        </>
      )}

      {/* Navigates to the export page (2026-08-09 design doc, decision 1/2) —
          real file generation stays inert there (decision 9); this button is
          just the entry point, no longer the inert shell M13 shipped. */}
      <span className="flex-1" />
      <Button variant="secondary" type="button" size="sm" compact onClick={onExport}>
        Exporter
      </Button>
      <NewMatrixMenu
        canDeriveFromThis={canDeriveFromThis}
        onCreateBlank={onCreateMatrix}
        onDeriveFromThis={onDeriveFromThis}
        deriveError={deriveError}
      />
    </div>
  );
}
